import { useRef, useCallback } from 'react';
import { ChevronUp, ChevronDown, GripHorizontal } from 'lucide-react';
import { useLayoutPreferences, SPLIT_PRESETS } from '@/hooks/useLayoutPreferences';

export function LayoutResizeHandle() {
  const { isCompact, splitRatio, setSplitRatio } = useLayoutPreferences();
  const isDragging = useRef(false);
  const handleRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || !handleRef.current) return;
    const parent = handleRef.current.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const ratio = ((e.clientY - rect.top) / rect.height) * 100;
    setSplitRatio(ratio);
  }, [setSplitRatio]);

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const cycleUp = useCallback(() => {
    if (splitRatio > SPLIT_PRESETS.middle + 2) {
      setSplitRatio('middle');
    } else {
      setSplitRatio('up');
    }
  }, [splitRatio, setSplitRatio]);

  const cycleDown = useCallback(() => {
    if (splitRatio < SPLIT_PRESETS.middle - 2) {
      setSplitRatio('middle');
    } else {
      setSplitRatio('down');
    }
  }, [splitRatio, setSplitRatio]);

  // Only render in compact mode
  if (!isCompact) return null;

  return (
    <div
      ref={handleRef}
      className="relative flex items-center shrink-0 h-6 select-none touch-none group/handle z-10 bg-surface-container"
    >
      {/* Up preset button */}
      <button
        onClick={cycleUp}
        className="flex items-center justify-center w-8 h-full text-on-surface-variant hover:text-on-surface hover:bg-on-surface-variant/8 rounded transition"
        title="Shrink list"
        aria-label="Shrink list"
      >
        <ChevronUp size={14} />
      </button>

      {/* Draggable center area */}
      <div
        className="flex-1 flex items-center justify-center h-full cursor-row-resize"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="flex items-center gap-0.5">
          <GripHorizontal size={16} className="text-outline-variant group-hover/handle:text-outline transition" />
        </div>
      </div>

      {/* Down preset button */}
      <button
        onClick={cycleDown}
        className="flex items-center justify-center w-8 h-full text-on-surface-variant hover:text-on-surface hover:bg-on-surface-variant/8 rounded transition"
        title="Expand list"
        aria-label="Expand list"
      >
        <ChevronDown size={14} />
      </button>

      {/* Visual separator line */}
      <div className="absolute inset-x-0 top-0 h-px bg-outline-variant/50" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-outline-variant/50" />
    </div>
  );
}
